/**
 * SOLID Review
 * S — Strong. One concern: the room on the server, as the contracted `ClientRoomServer` port.
 * O — Good. A new room op (create and join arrive in FE2) is one method over the adapter.
 * L — Strong. Implements `IPortClientRoomServer` exactly.
 * I — Strong. Exactly the port's ops, each one call to the adapter.
 * D — Strong. The Application's adapter interface; the Apollo adapter arrives by constructor.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadRoomRead } from '../../domain/ports/schemas/TableMoves';
import type { IPayloadRoomRoster } from '../../domain/ports/schemas/TableEvents';
import type { IPortClientRoomServer, IPayloadServerRoomCode } from '../../application/ports/contracts';
import type { IAdapterApolloRoom } from '../../application/ports/IAdapterApolloRoom';
import type {
  ICommandLeaveRoom, IEntityRawRoom, IEntityRoomCodeLookup, IEntityRejoinRoomByCode, IEntityActiveRoomForPlayer,
} from '../../application/ports/apolloShapes';
import type {
  IPayloadSeat, IPayloadLateJoin, IPayloadRejoinRequest, IPayloadHost, IPayloadPlayer, IPayloadSeatRoom, IPayloadRoomCodeLookup,
  IPayloadRejoin, IPayloadActiveRoomForPlayer,
} from '../../domain/ports/schemas/SessionMoves';
import { ok } from '../../shared/Result';

/**
 * THE ROOM GATEWAY (frontend strict-ports plan, FE1, 2026-10-03), over `ApolloRoomAdapter`. Lists are
 * copied: the adapter's are readonly, and the port's answers are fresh JSON. FE2 adds the five ways to a
 * seat — create, join, late join, rejoin by code, and the cold login's "where does the server seat me".
 * The adapter's failure is the port's since frontend-infrastructure-oo P3 (§28.288): it passes through.
 */
export class ClientRoomServerAdapter implements IPortClientRoomServer {
  constructor(private readonly rooms: IAdapterApolloRoom) {}

  async getRoom(_ctx: IContextRequest, read: IPayloadRoomRead) {
    const room = await this.rooms.getRoom(read.roomId);
    return room.success ? ok(this.rosterOf(room.value)) : room;
  }

  async getRoomByCode(_ctx: IContextRequest, query: IPayloadServerRoomCode) {
    const lookup = await this.rooms.getRoomByCode(query.roomCode);
    return lookup.success ? ok(this.lookupOf(lookup.value)) : lookup;
  }

  async leaveRoom(_ctx: IContextRequest, seat: { readonly roomId: string; readonly playerId: string }) {
    const command: ICommandLeaveRoom = { roomId: seat.roomId, playerId: seat.playerId };
    const left = await this.rooms.leaveRoom(command);
    return left.success ? ok(undefined) : left;
  }

  async createRoom(_ctx: IContextRequest, request: IPayloadHost) {
    const room = await this.rooms.createRoom(request.hostPlayerId);
    return room.success ? ok(this.seatRoomOf(room.value)) : room;
  }

  async joinRoom(_ctx: IContextRequest, seat: IPayloadSeat) {
    const room = await this.rooms.joinRoom(seat.roomId, seat.playerId);
    return room.success ? ok(this.seatRoomOf(room.value)) : room;
  }

  async lateJoinRoom(_ctx: IContextRequest, request: IPayloadLateJoin) {
    const room = await this.rooms.lateJoinRoom(request.roomId, request.playerId, request.displayName, request.matchCredits);
    return room.success ? ok(this.seatRoomOf(room.value)) : room;
  }

  async rejoinRoomByCode(_ctx: IContextRequest, request: IPayloadRejoinRequest) {
    const rejoin = await this.rooms.rejoinRoomByCode(request.roomCode, request.playerId, request.displayName, request.email);
    return rejoin.success ? ok(this.rejoinOf(rejoin.value)) : rejoin;
  }

  async getActiveRoomForPlayer(_ctx: IContextRequest, request: IPayloadPlayer) {
    const room = await this.rooms.getActiveRoomForPlayer(request.playerId);
    return room.success ? ok(this.activeRoomOf(room.value)) : room;
  }

  private rosterOf(room: IEntityRawRoom): IPayloadRoomRoster {
    return { id: room.id, players: [...room.players] };
  }

  private lookupOf(lookup: IEntityRoomCodeLookup | null): IPayloadRoomCodeLookup | null {
    return lookup === null ? null : { ...lookup, suspendedPlayers: [...lookup.suspendedPlayers] };
  }

  /** A `Room` answer as fresh JSON: every list copied, nothing else touched — the contract parses the rest. */
  private seatRoomOf(room: IEntityRawRoom): IPayloadSeatRoom {
    return {
      id: room.id,
      roomCode: room.roomCode,
      playerIds: [...room.playerIds],
      players: room.players.map((p) => ({ id: p.id, displayName: p.displayName })),
      status: room.status,
    };
  }

  private rejoinOf(rejoin: IEntityRejoinRoomByCode): IPayloadRejoin {
    return {
      playerId: rejoin.playerId,
      roomId: rejoin.roomId,
      roomCode: rejoin.roomCode,
      roomStatus: rejoin.roomStatus,
      players: rejoin.players.map((p) => ({ id: p.id, displayName: p.displayName })),
    };
  }

  private activeRoomOf(room: IEntityActiveRoomForPlayer | null): IPayloadActiveRoomForPlayer | null {
    return room === null ? null : { roomId: room.roomId, roomCode: room.roomCode, roomStatus: room.roomStatus, playerCount: room.playerCount };
  }
}
